import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Card } from '@/components/ui'
import { H3 } from '@/components/ui/typography'
import { formatCmsDate } from '@/cms/formatDate'
import VenueLink from '@/components/cards/VenueLink'
import { cmsMediaSrcSet } from '@/utils/cmsMediaUrl'
import { uuidFromUploadPath } from '@/utils/mediaPath'
import { safeExternalUrl } from '@/utils/safeExternalUrl'

/**
 * The `sizes` for a lecture photo.
 *
 * The compact card is used in the homepage's 3-column grid (a column is
 * ≈33vw); the regular card in the archive's 2-column grid (≈50vw). Both sit
 * inside the 1200px container, where those fractions OVERSTATE the column —
 * which is the safe direction: a `sizes` that is too small makes the browser
 * upscale a too-small file.
 */
const COMPACT_PHOTO_SIZES = '(min-width: 768px) 33vw, 100vw'
const REGULAR_PHOTO_SIZES = '(min-width: 768px) 50vw, 100vw'

/**
 * Normalized lecture shape — every consumer passes their own
 * pre-localized data through this contract.
 *
 *   - LecturesFeature (home)    → normalises from CmsLectureIndexEntry
 *   - LecturesPage (archive)    → normalises from Lecture
 * Both shapes live in different files; normalising at the card
 * boundary keeps this component free of CMS coupling.
 */
export interface NormalizedLecture {
  slug: string
  title: string
  summary: string
  audience: 'teacher' | 'parent' | 'student'
  /** ISO 8601 YYYY-MM-DD */
  date: string
  venue: string
  photoUrl?: string
  photoAlt?: string
  photoWidth?: number
  photoHeight?: number
  externalUrl?: string
  /**
   * Official website of the venue / host organisation, e.g.
   * "https://www.yckmc.edu.hk/".
   *
   * Added 2026-10-20. When present, the venue name renders as an external
   * link that opens in a new tab. When absent, it stays plain text — a
   * lecture hosted somewhere without a public website must not produce a
   * dead link.
   */
  venueUrl?: string
}

type LectureCardSize = 'compact' | 'regular'

interface LectureCardProps {
  lecture: NormalizedLecture
  /**
   * `compact` — used in dense 3-col homepage grids. Title 18px, summary
   *             line-clamp-2, whole card is a single Link.
   * `regular` — used in the 2-col archive. Title 20→24px, full summary,
   *             cover is the Link + body has a primary CTA + optional
   *             external `<a>` (so an external URL doesn't nest inside
   *             the wrapping Link, which is invalid HTML).
   */
  size?: LectureCardSize
  /** i18n label for the primary CTA — e.g. "閱讀全文" / "Read full talk". */
  ctaLabel: string
  /** i18n label for the secondary external-link CTA. */
  externalCtaLabel?: string
  /** Pre-translated audience badge, e.g. "家長場" / "For parents". */
  audienceBadgeLabel: string
  /** i18n locale used for toLocaleDateString. */
  locale: string
  /**
   * i18n suffix for the venue link's accessible name, e.g. "（外部連結）".
   * Announced to screen readers so the new tab is not a surprise.
   */
  externalVenueLabel?: string
  /**
   * Extra classes for the card surface. Use it to override the default
   * `bg-surface-2` (e.g. `bg-white!` when the card sits on a cream
   * section and needs to read as a white panel).
   */
  className?: string
}

/**
 * LectureCard — single source of truth for the YADA lecture card.
 *
 * Phase 1 mobile overhaul (2026-10-13): extracted from two inline copies
 * that had drifted apart in title size, summary density, and CTA layout.
 * Both call sites now share the same markup and the same Card `density`.
 */
export default function LectureCard({
  lecture,
  size = 'regular',
  ctaLabel,
  externalCtaLabel,
  audienceBadgeLabel,
  locale,
  externalVenueLabel,
  className = '',
}: LectureCardProps) {
  const isCompact = size === 'compact'
  const detailHref = `/lectures/${lecture.slug}`
  // The lecture's external URL comes from the CMS, so it is validated before
  // it is allowed to become an href — a `javascript:` value would run script
  // in every visitor's browser. When the value does not pass, the external
  // CTA is not rendered at all: a card with no external link is better than
  // a card with an executable one.
  const externalHref = safeExternalUrl(lecture.externalUrl)
  // A lecture photo is a media-library upload, so the responsive ladder can
  // serve it — the card draws it in a third or a half of the grid column,
  // while the file behind it is often the full-size original. A photo whose
  // path is not a library upload keeps its plain <img>.
  const photoSrcSet = lecture.photoUrl
    ? cmsMediaSrcSet(uuidFromUploadPath(lecture.photoUrl))
    : undefined

  // 2026-10-16: was a try/catch around the parse, which never fired —
  // `new Date(bad)` returns an Invalid Date object instead of throwing, and
  // its `toLocaleDateString()` returns the string "Invalid Date". Now that a
  // real lecture in prod carried `event_date = "2026年10月13日"`, the archive
  // card rendered those literal words. See src/cms/formatDate.ts.
  const formattedDate = formatCmsDate(lecture.date, locale)

  const metaRow = (
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2.5 py-0.5 rounded-yada-full text-xs font-semibold">
        <Icon icon="lucide:megaphone" className="text-xs" />
        {audienceBadgeLabel}
      </span>
      {lecture.venue && (
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
          <Icon icon="lucide:building-2" className="text-xs" />
          {/*
            2026-10-20: link the venue to the host organisation's official
            website.

            `linkable` is false in the compact variant because that card wraps
            the whole thing in a <Link> — an <a> inside an <a> is invalid HTML
            and the browser's parser closes the outer anchor early, which
            breaks the card. The regular variant has no wrapping link, so the
            venue can be a real anchor there.
          */}
          <VenueLink
            venue={lecture.venue}
            url={lecture.venueUrl}
            linkable={!isCompact}
            externalLabel={externalVenueLabel}
            className="inline-flex items-center gap-1"
            linkClassName="hover:text-primary hover:underline underline-offset-2 transition-colors"
          />
        </span>
      )}
      <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
        <Icon icon="lucide:calendar" className="text-xs" />
        <time dateTime={lecture.date}>{formattedDate}</time>
      </span>
    </div>
  )

  const titleEl = (
    <H3
      className={
        isCompact
          ? 'line-clamp-3 mb-2'
          : 'text-xl md:text-2xl leading-tight mb-2'
      }
    >
      {lecture.title || lecture.slug}
    </H3>
  )

  const summaryEl = lecture.summary ? (
    <p
      className={
        isCompact
          ? 'text-sm text-ink-soft line-clamp-2 mb-3'
          : 'text-base text-ink-soft leading-relaxed mb-4'
      }
    >
      {lecture.summary}
    </p>
  ) : null

  /* ---------------- compact: whole card is one Link ---------------- */
  if (isCompact) {
    return (
      <Link
        to={detailHref}
        className="block h-full focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-xl"
        aria-label={lecture.title || lecture.slug}
      >
        <Card hover bordered density="compact" className={`h-full overflow-hidden flex flex-col ${className}`}>
          {lecture.photoUrl && (
            <img
              src={lecture.photoUrl}
              srcSet={photoSrcSet}
              sizes={photoSrcSet ? COMPACT_PHOTO_SIZES : undefined}
              alt={lecture.photoAlt || lecture.title}
              loading="lazy"
              decoding="async"
              width={lecture.photoWidth || undefined}
              height={lecture.photoHeight || undefined}
              className="block w-full aspect-[3/2] object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            {metaRow}
            {titleEl}
            {summaryEl}
            <div className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
              <span>{ctaLabel}</span>
              <Icon icon="lucide:arrow-right" />
            </div>
          </div>
        </Card>
      </Link>
    )
  }

  /* ---------------- regular: cover link + body CTAs ---------------- */
  return (
    <Card
      hover
      bordered
      density="regular"
      className={`h-full overflow-hidden flex flex-col ${className}`}
    >
      {lecture.photoUrl && (
        <Link
          to={detailHref}
          aria-label={lecture.title}
          className="block w-full aspect-[3/2] overflow-hidden border-b border-border bg-surface focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
        >
          <img
            src={lecture.photoUrl}
            srcSet={photoSrcSet}
            sizes={photoSrcSet ? REGULAR_PHOTO_SIZES : undefined}
            alt={lecture.photoAlt || lecture.title}
            loading="lazy"
            decoding="async"
            width={lecture.photoWidth || undefined}
            height={lecture.photoHeight || undefined}
            className="block w-full h-full object-cover object-top"
          />
        </Link>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {metaRow}
        {titleEl}
        {summaryEl}

        <div className="flex flex-wrap items-center gap-3 mt-auto">
          <Link
            to={detailHref}
            className="inline-flex items-center gap-2 bg-primary text-surface px-5 py-2.5 rounded-yada-full text-sm font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            <Icon icon="lucide:book-open-text" />
            {ctaLabel}
            <Icon icon="lucide:arrow-right" />
          </Link>
          {externalHref && externalCtaLabel && (
            <a
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              <Icon icon="lucide:external-link" />
              {externalCtaLabel}
            </a>
          )}
        </div>
      </div>
    </Card>
  )
}
